import { useCallback, useEffect, useRef, useState } from "react";

import { layoutViewportWidth, subscribeViewportMetrics } from "@/lib/viewport";

const STORAGE_KEY = "twido:right-rail-width";
const MIN_WIDTH = 280;
const MIN_MAIN_WIDTH = 400;

function defaultWidthForViewport() {
  return window.matchMedia("(min-width: 1280px)").matches ? 360 : 320;
}

function readShellMetrics() {
  const root = document.documentElement;
  const rootStyles = getComputedStyle(root);
  const parseLength = (raw: string, fallback: number) => {
    const trimmed = raw.trim();
    if (!trimmed) return fallback;
    if (trimmed.endsWith("px")) return Number.parseFloat(trimmed);
    if (trimmed.endsWith("rem")) {
      const rootFont = Number.parseFloat(rootStyles.fontSize) || 16;
      return Number.parseFloat(trimmed) * rootFont;
    }
    const parsed = Number.parseFloat(trimmed);
    return Number.isFinite(parsed) ? parsed : fallback;
  };

  return {
    padding: parseLength(rootStyles.getPropertyValue("--shell-padding"), 20),
    gap: parseLength(rootStyles.getPropertyValue("--shell-gap"), 16),
    gutter: parseLength(rootStyles.getPropertyValue("--rail-gutter-width"), 52),
  };
}

function maxWidthForViewport(viewportWidth = layoutViewportWidth()) {
  const { padding, gap, gutter } = readShellMetrics();
  const available =
    viewportWidth - padding * 2 - gutter - gap - MIN_MAIN_WIDTH;
  return Math.max(MIN_WIDTH, Math.min(560, available));
}

function loadStoredWidth() {
  try {
    const raw = sessionStorage.getItem(STORAGE_KEY);
    if (!raw) return null;
    const parsed = Number.parseInt(raw, 10);
    return Number.isFinite(parsed) ? parsed : null;
  } catch {
    return null;
  }
}

function clampWidth(width: number) {
  return Math.round(
    Math.min(maxWidthForViewport(), Math.max(MIN_WIDTH, width)),
  );
}

function applyWidth(width: number) {
  document.documentElement.style.setProperty("--right-rail-width", `${width}px`);
}

export function useRightRailResize(options?: { resizable?: boolean }) {
  const resizable = options?.resizable ?? true;
  const [width, setWidth] = useState(() => {
    const stored = loadStoredWidth();
    return clampWidth(stored ?? defaultWidthForViewport());
  });
  const widthRef = useRef(width);
  widthRef.current = width;

  useEffect(() => {
    applyWidth(width);
  }, [width]);

  useEffect(() => {
    const onViewportChange = () => {
      setWidth((current) => clampWidth(current));
    };

    return subscribeViewportMetrics(onViewportChange);
  }, []);

  const persistWidth = useCallback((next: number) => {
    try {
      sessionStorage.setItem(STORAGE_KEY, String(next));
    } catch {
      /* ignore quota / private mode */
    }
  }, []);

  const onResizePointerDown = useCallback(
    (event: React.PointerEvent<HTMLButtonElement>) => {
      if (!resizable) return;
      event.preventDefault();

      const handle = event.currentTarget;
      const startX = event.clientX;
      const startWidth = widthRef.current;
      handle.setPointerCapture(event.pointerId);
      document.body.style.cursor = "col-resize";
      document.body.style.userSelect = "none";

      const onPointerMove = (moveEvent: PointerEvent) => {
        const delta = startX - moveEvent.clientX;
        const next = clampWidth(startWidth + delta);
        widthRef.current = next;
        applyWidth(next);
        setWidth(next);
      };

      const onPointerUp = (upEvent: PointerEvent) => {
        handle.releasePointerCapture(upEvent.pointerId);
        document.body.style.cursor = "";
        document.body.style.userSelect = "";
        persistWidth(widthRef.current);
        handle.removeEventListener("pointermove", onPointerMove);
        handle.removeEventListener("pointerup", onPointerUp);
        handle.removeEventListener("pointercancel", onPointerUp);
      };

      handle.addEventListener("pointermove", onPointerMove);
      handle.addEventListener("pointerup", onPointerUp);
      handle.addEventListener("pointercancel", onPointerUp);
    },
    [resizable, persistWidth],
  );

  const resetWidth = useCallback(() => {
    const next = clampWidth(defaultWidthForViewport());
    setWidth(next);
    persistWidth(next);
  }, [persistWidth]);

  return {
    width,
    onResizePointerDown,
    resetWidth,
    minWidth: MIN_WIDTH,
    maxWidth: maxWidthForViewport(),
  };
}
